import React from 'react'
import {log} from '@/logger'

import {api} from '../services/api'
import {PersonTaggingDiagnostics} from '../types'
import {FetchStatus} from './useRecognitionAttempts'

interface Params {
  personId: number | null
  clientId: number | null
  // Undefined means "let the server pick the most recent season with data".
  // A camp's seasonID is not guaranteed to track the calendar year, so the
  // client must never default this itself.
  season?: number
  // Gate the fetch so nothing is requested before the operator submits the
  // lookup form, or while the employee / ConfigCat gate is closed.
  enabled: boolean
}

export interface PersonTaggingDiagnosticsState {
  status: FetchStatus
  data: PersonTaggingDiagnostics | null
  // personId → display name, for resolving who applied each manual tag.
  nameMap: Map<number, string>
  errorMessage: string | null
}

/**
 * Owns the per-camper FR tagging-diagnostics fetch and the follow-on
 * tagger-name resolution for one (person, camp, season).
 *
 * Behavior contract mirrors useRecognitionAttempts:
 * - A `cancelled` flag stale-guards every `set*` so a resolution landing after
 *   unmount / dependency change is discarded.
 * - Name resolution is a display enhancement: a failure falls back to an empty
 *   name map (the UI shows "Person <id>") plus a breadcrumb, and never flips
 *   the page into the error state.
 */
export const usePersonTaggingDiagnostics = ({
  personId,
  clientId,
  season,
  enabled,
}: Params): PersonTaggingDiagnosticsState => {
  const [status, setStatus] = React.useState<FetchStatus>('idle')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [data, setData] = React.useState<PersonTaggingDiagnostics | null>(null)
  const [nameMap, setNameMap] = React.useState<Map<number, string>>(
    () => new Map(),
  )

  React.useEffect(() => {
    // personId/clientId are null until the operator has entered both, so the
    // null check is part of the gate rather than a separate guard.
    if (!enabled || personId === null || clientId === null) return undefined

    let cancelled = false
    setStatus('pending')
    setErrorMessage(null)
    setNameMap(new Map())

    const fetchData = async (): Promise<void> => {
      const response = await api.faces.getPersonTaggingDiagnostics({
        personId,
        clientId,
        season,
      })

      if (cancelled) return

      if (response.result !== 'success') {
        setData(null)
        setErrorMessage(response.error.message)
        setStatus('rejected')
        log.breadcrumb('Failed to load person tagging diagnostics', {
          personId,
          clientId,
        })
        return
      }

      setData(response.data)
      setStatus('resolved')

      // Close-call rows carry only the tagger's personId; resolve display
      // names via the persons GetNames batch wrapper (de-dupes and chunks
      // internally, so passing duplicates across both lists is fine).
      const taggerIds = [
        ...response.data.closeCalls.accepted,
        ...response.data.closeCalls.nearMiss,
      ]
        .map((call) => call.taggingPersonId)
        .filter((id): id is number => id !== null)

      if (taggerIds.length === 0) return

      const namesResponse = await api.persons.getNames({
        clientID: clientId,
        ids: taggerIds,
      })

      if (cancelled) return

      if (namesResponse.result === 'success') {
        setNameMap(namesResponse.data)
      } else {
        log.breadcrumb('Failed to resolve close-call tagger names', {
          personId,
          clientId,
        })
      }
    }

    fetchData()

    return (): void => {
      cancelled = true
    }
  }, [enabled, personId, clientId, season])

  return {status, data, nameMap, errorMessage}
}
